import { Check, Copy, X } from "lucide-react";
import { type FormEvent, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { track } from "@/lib/analytics";
import {
  type ReportInviteRecord,
  useCreateReportInvite,
  useReportInvites,
  useRevokeReportInvite,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

interface InvitePanelProps {
  assessmentId: string;
}

/**
 * `expires_at` is a Postgres `timestamptz`, which PostgREST serializes with a
 * UTC offset, so `Date` parses it unambiguously. If it ever arrives in a shape
 * `Date` cannot read, we treat the invite as still live and let the backend be
 * the authority: it answers 410 on accept. Claiming "expired" on a parse
 * failure would be the worse error.
 */
function isExpired(expiresAt: string): boolean {
  const deadline = new Date(expiresAt).getTime();
  return Number.isNaN(deadline) ? false : deadline <= Date.now();
}

/**
 * Expiry bounds redemption only (an accepted invite never lapses) so only a
 * still-pending invite can read "expired". Revoked and expired are both dead
 * ends and share the muted tone. An unrecognised status falls through to its
 * raw value, muted, rather than being silently relabelled.
 */
function inviteState(status: string, expired: boolean): string {
  return status === "pending" && expired ? "expired" : status;
}

/** "accepted" is backend vocabulary; what the owner cares about is that the
 *  colleague can now read the report. */
function stateLabel(state: string): string {
  return state === "accepted" ? "has access" : state;
}

function pillClasses(state: string): string {
  if (state === "accepted") {
    return "bg-accent text-accent-foreground";
  }
  if (state === "pending") {
    return "bg-accent-light/60 text-accent";
  }
  return "bg-primary/10 text-muted-foreground";
}

/** Small styled span rather than a vendored Badge: the codebase has no Badge. */
function StatusPill({ status, expired }: { status: string; expired: boolean }) {
  const state = inviteState(status, expired);
  return (
    <span
      className={cn(
        "rounded px-2 py-0.5 text-[11px] font-bold tracking-wide uppercase",
        pillClasses(state),
      )}
    >
      {stateLabel(state)}
    </span>
  );
}

interface InviteRowProps {
  invite: ReportInviteRecord;
  onRevoke: (inviteId: string) => void;
  revoking: boolean;
}

function InviteRow({ invite, onRevoke, revoking }: InviteRowProps) {
  const [copied, setCopied] = useState(false);
  const expired = isExpired(invite.expires_at);

  // Gate the copy affordance on the token, never on `status === "pending"`:
  // re-inviting someone who already accepted returns their accepted row, which
  // carries a null token. The backend NULLs invite_token on every accepted and
  // revoked row, so a link exists exactly when there is something to copy.
  const inviteUrl = invite.invite_token
    ? `${window.location.origin}/report/invite/${invite.invite_token}`
    : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Clipboard access is denied on insecure origins and by some browsers.
      logger.warn("[invite] clipboard write failed", error);
    }
  };

  return (
    <div className="border-border/60 flex flex-wrap items-center justify-between gap-3 border-b py-3 last:border-b-0">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="text-primary truncate text-sm font-medium">{invite.invited_email}</span>
        <StatusPill status={invite.status} expired={expired} />
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {inviteUrl && !expired && (
          <Button variant="outline" size="sm" onClick={() => void handleCopy()}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy link"}
          </Button>
        )}
        {invite.status !== "revoked" && (
          <Button
            variant="ghost"
            size="sm"
            disabled={revoking}
            onClick={() => onRevoke(invite.id)}
            aria-label={`Revoke invite for ${invite.invited_email}`}
          >
            <X className="h-4 w-4" />
            Revoke
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Renders the inline notice after an invite is created, driven by the
 * `email_delivery` field on the create response. `sent` means the backend
 * emailed the link, `failed` means it tried and could not, and `disabled`
 * means email delivery is not configured; the last two both steer the owner
 * to the copy-link fallback below.
 */
function DeliveryNotice({ delivery, email }: { delivery: string; email: string }) {
  if (delivery === "sent") {
    return (
      <p className="mt-3 text-sm text-green-600">
        Invite sent. We’ve emailed {email} a link to this report.
      </p>
    );
  }
  if (delivery === "failed") {
    return (
      <p className="text-destructive mt-3 text-sm">
        We couldn’t send the invite email. Copy the link below and send it to {email} yourself.
      </p>
    );
  }
  return (
    <p className="text-muted-foreground mt-3 text-sm">
      Invite created. Copy the link below and send it to {email} yourself.
    </p>
  );
}

/**
 * Owner-only invite management for a report. The parent renders this solely
 * when `record.share` is present, which the backend populates only for the
 * report's owner.
 *
 * Email delivery now exists: when the backend is configured for it (a Resend
 * key, a sender address, and a public URL), creating an invite also emails the
 * recipient the link. The create response reports the outcome on
 * `email_delivery` (`sent` / `failed` / `disabled`), which `DeliveryNotice`
 * surfaces inline. Copy-link stays the universal fallback: it always works,
 * and the `failed`/`disabled` outcomes point the owner to it, so the panel
 * never depends on email being set up. We deliberately do NOT reach for
 * Supabase's `invite_user_by_email`: it invites a person to the whole
 * application and 409s when they already have an account, conflating app
 * access with report access.
 */
export function InvitePanel({ assessmentId }: InvitePanelProps) {
  const invitesQuery = useReportInvites(assessmentId);
  const createInvite = useCreateReportInvite(assessmentId);
  const revokeInvite = useRevokeReportInvite(assessmentId);
  const [email, setEmail] = useState("");

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      return;
    }
    createInvite.mutate(
      { email: trimmed },
      {
        onSuccess: () => {
          track("report_shared", { method: "invite" });
          setEmail("");
        },
      },
    );
  };

  const invites = invitesQuery.data ?? [];

  return (
    <Card className="mt-5">
      <CardContent className="pt-6">
        <div>
          <label htmlFor="invite-email" className="text-primary text-sm font-bold">
            Invite a colleague
          </label>
          <p className="text-muted-foreground mt-1 max-w-[60ch] text-sm">
            The invite is bound to the address you enter: only that person can open the report, and
            only once they sign in. We’ll email them the link when delivery is set up, and you can
            always copy it below and send it yourself.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 flex items-center gap-2">
          <Input
            id="invite-email"
            type="email"
            required
            placeholder="colleague@company.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={createInvite.isPending}
          />
          <Button type="submit" className="shrink-0" disabled={createInvite.isPending}>
            {createInvite.isPending ? "Inviting…" : "Invite"}
          </Button>
        </form>

        {/* Inviting someone who already accepted is a success, not an error: the
            backend hands back their existing accepted row. Say so, or the form
            just clears and the owner is left wondering whether it worked. */}
        {createInvite.isSuccess && createInvite.data?.status === "accepted" && (
          <p className="text-muted-foreground mt-3 text-sm">
            {createInvite.data.invited_email} already has access to this report.
          </p>
        )}
        {/* A fresh mint or an idempotent re-invite of a pending address: the
            backend attempts the email and reports the outcome on
            `email_delivery`. Reading it straight off `createInvite.data` means
            each successful submit refreshes this line. */}
        {createInvite.isSuccess && createInvite.data && createInvite.data.status !== "accepted" && (
          <DeliveryNotice
            delivery={createInvite.data.email_delivery}
            email={createInvite.data.invited_email}
          />
        )}
        {createInvite.isError && (
          <p className="text-destructive mt-3 text-sm">
            Couldn’t create the invite. Check the address and try again.
          </p>
        )}
        {revokeInvite.isError && (
          <p className="text-destructive mt-3 text-sm">
            Couldn’t revoke that invite. Please try again.
          </p>
        )}

        {invites.length > 0 && (
          <div className="mt-5">
            {invites.map((invite) => (
              <InviteRow
                key={invite.id}
                invite={invite}
                revoking={revokeInvite.isPending}
                onRevoke={(inviteId) => revokeInvite.mutate({ inviteId })}
              />
            ))}
          </div>
        )}

        {invitesQuery.isError && (
          <p className="text-destructive mt-3 text-sm">
            Couldn’t load the invites for this report.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
